import { create } from 'zustand'
import { api, ApiError, clearTokens, hasSession, onAuthLost, setTokens } from '../api/client.ts'
import { loginWithPasskey } from '../lib/passkeys.ts'
import { connectSocket, disconnectSocket } from '../realtime/socket.ts'
import type { User } from '../api/types.ts'

type AuthState = {
  user: User | null
  /** «checking» — пока проверяем сохранённый вход, чтобы не мигать формой входа */
  status: 'checking' | 'authenticated' | 'anonymous'
  restore: () => Promise<void>
  /** Возвращает пропуск, если нужен второй ключ, иначе null */
  login: (email: string, password: string) => Promise<string | null>
  loginWithCode: (ticket: string, code: string) => Promise<void>
  /** Войти ключом телефона: лицом, отпечатком или кодом разблокировки */
  loginByPasskey: () => Promise<void>
  /** Задать новый пароль по коду восстановления */
  resetPassword: (code: string, password: string) => Promise<void>
  register: (input: {
    email: string
    password: string
    displayName: string
    familyName?: string
    inviteCode?: string
  }) => Promise<void>
  logout: () => void
  /** Обновить свои данные после правки профиля — без повторного входа */
  setUser: (user: User) => void
}

type AuthResponse = { accessToken: string; refreshToken: string; user: User }

/**
 * Кто вошёл — помним и без связи.
 *
 * Приложение на телефоне часто открывают там, где сети ещё нет или она
 * еле живая: в лифте, в метро, в первую секунду после разблокировки.
 * Раньше в этот момент проверка входа не проходила, и человека
 * выбрасывало на форму входа. Теперь приложение открывается как было,
 * а проверка повторяется сама, когда связь появится.
 */
const USER_KEY = 'family-user'

function rememberUser(user: User | null): void {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_KEY)
  } catch {
    /* без памяти: при следующем запуске без связи придётся подождать */
  }
}

function rememberedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

/** Сервер отказал во входе — а не просто не ответил. */
const isRejected = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 401 || error.code === 'SESSION_EXPIRED')

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'checking',

  /** Вызывается один раз при запуске: вход должен переживать перезагрузку. */
  restore: async () => {
    if (!hasSession()) {
      set({ status: 'anonymous', user: null })
      return
    }

    const cached = rememberedUser()
    if (cached) {
      // Сразу пускаем внутрь: вход есть, а свежие данные подтянутся следом
      set({ user: cached, status: 'authenticated' })
      connectSocket()
    }

    let delay = 2000
    const check = async (): Promise<void> => {
      try {
        const data = await api<{ user: User }>('/api/auth/me')
        rememberUser(data.user)
        set({ user: data.user, status: 'authenticated' })
        if (!cached) connectSocket()
      } catch (error) {
        if (isRejected(error)) {
          disconnectSocket()
          clearTokens()
          rememberUser(null)
          set({ status: 'anonymous', user: null })
          return
        }
        // Нет связи или сервер перезапускается — пробуем ещё, реже и реже
        window.setTimeout(() => void check(), delay)
        delay = Math.min(delay * 2, 30_000)
      }
    }

    await check()
  },

  /**
   * Вход паролем.
   *
   * Если включён второй ключ, сервер отдаёт не доступ, а пропуск:
   * по нему предъявляют одноразовый код. Возвращаем пропуск наружу —
   * страница входа покажет поле для кода.
   */
  login: async (email, password) => {
    const data = await api<AuthResponse | { needCode: true; ticket: string }>(
      '/api/auth/login',
      { method: 'POST', body: { email, password } },
    )

    if ('needCode' in data) return data.ticket

    setTokens(data.accessToken, data.refreshToken)
    rememberUser(data.user)
    set({ user: data.user, status: 'authenticated' })
    connectSocket()
    return null
  },

  /** Вторая половина входа: код из аутентификатора. */
  loginWithCode: async (ticket, code) => {
    const data = await api<AuthResponse>('/api/auth/login/code', {
      method: 'POST',
      body: { ticket, code },
    })
    setTokens(data.accessToken, data.refreshToken)
    rememberUser(data.user)
    set({ user: data.user, status: 'authenticated' })
    connectSocket()
  },

  register: async (input) => {
    const data = await api<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: input,
    })
    setTokens(data.accessToken, data.refreshToken)
    rememberUser(data.user)
    set({ user: data.user, status: 'authenticated' })
    connectSocket()
  },

  /**
   * Войти ключом телефона — лицом, отпечатком или кодом разблокировки.
   *
   * Почту не спрашиваем: телефон сам предложит подходящий ключ, кто
   * пришёл — выяснится по подписи.
   */
  loginByPasskey: async () => {
    const data = (await loginWithPasskey()) as AuthResponse
    setTokens(data.accessToken, data.refreshToken)
    rememberUser(data.user)
    set({ user: data.user, status: 'authenticated' })
    connectSocket()
  },

  /**
   * Новый пароль по коду от взрослого.
   *
   * Писем приложение не отправляет, поэтому «забыли пароль?» работает
   * не ссылкой на почту, а кодом, который выдаёт кто-то из семьи.
   * Сервер сразу впускает: человек только что доказал, что это он.
   */
  resetPassword: async (code, password) => {
    const data = await api<AuthResponse>('/api/auth/reset', {
      method: 'POST',
      body: { code, password },
    })
    setTokens(data.accessToken, data.refreshToken)
    rememberUser(data.user)
    set({ user: data.user, status: 'authenticated' })
    connectSocket()
  },

  setUser: (user) => {
    rememberUser(user)
    set({ user })
  },

  logout: () => {
    disconnectSocket()
    clearTokens()
    rememberUser(null)
    set({ user: null, status: 'anonymous' })
  },
}))

// Ключ обновления протух или был погашен — возвращаем к форме входа
onAuthLost(() => {
  disconnectSocket()
  rememberUser(null)
  useAuth.setState({ user: null, status: 'anonymous' })
})
